import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getUtm, track } from "../lib/analytics";
import { useTranslation } from "react-i18next";
import { currentLanguage } from "../i18n";
import { useMeta } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function AuthForm({ onSuccess, defaultMode = "signup" }) {
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null); // { mfa_token, methods }
  const [magicSent, setMagicSent] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMethod, setMfaMethod] = useState("");
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const finish = (data) => {
    login(data.token, data.user);
    if (data.user?.mfa_setup_required) {
      toast.info("Admin accounts require MFA — please set it up now.");
      navigate("/settings");
      return;
    }
    toast.success("Welcome back");
    onSuccess?.(data.user);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "magic") {
        await api.post("/auth/magic-link", { email });
        setMagicSent(true);
        return;
      }
      if (mode === "signup") {
        const { data } = await api.post("/auth/signup", { email, password, name, utm: getUtm(), newsletter_opt_in: newsletter, language: currentLanguage() });
        if (data.existing) {
          toast.info(data.message || "If an account exists for this email, we've sent a sign-in reminder to it.");
          setMode("login");
          return;
        }
        login(data.token, data.user);
        track("sign_up", { method: "password" });
        if (data.email_sent === false) {
          toast.error("We couldn't send your verification email. Please contact support@decodemydenial.com", { duration: 8000 });
        } else {
          toast.success("Account created");
        }
        onSuccess?.(data.user);
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) {
          const preferred = data.methods.includes("totp") ? "totp" : data.methods[0];
          setMfa(data);
          setMfaMethod(preferred);
          if (preferred === "email") await sendEmailCode(data.mfa_token);
          return;
        }
        finish(data);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "passwordless_account") {
        toast.info("This account signs in with email links — no password was ever set. We'll send you one.");
        setMode("magic");
      } else if (d === "password_reset_required") {
        toast.info("For your security, please sign in with an email link and set a fresh password.");
        setMode("magic");
      } else {
        toast.error(typeof d === "string" ? d : "Something went wrong");
      }
    } finally {
      setBusy(false);
    }
  };

  const sendEmailCode = async (token) => {
    try {
      await api.post("/auth/mfa/send-email-code", { mfa_token: token || mfa.mfa_token });
      toast.success("Code sent to your email");
    } catch { toast.error("Could not send email code"); }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code: mfaCode, method: mfaMethod });
      finish(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  if (mfa) {
    return (
      <form onSubmit={verifyMfa} className="space-y-4" data-testid="mfa-form">
        <p className="text-sm text-slate-600">This account has multi-factor authentication. Enter your code to continue.</p>
        <div className="flex gap-2 flex-wrap">
          {mfa.methods.map((m) => (
            <button type="button" key={m} onClick={() => { setMfaMethod(m); if (m === "email") sendEmailCode(); }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${mfaMethod === m ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-600"}`}
              data-testid={`mfa-method-${m}`}>
              {m === "totp" ? "Authenticator app" : m === "email" ? "Email code" : "Recovery code"}
            </button>
          ))}
        </div>
        {mfaMethod === "email" && (
          <button type="button" onClick={() => sendEmailCode()} className="text-xs text-emerald-700 font-medium hover:underline" data-testid="resend-email-code">Resend email code</button>
        )}
        <div>
          <Label htmlFor="mfa-code">{mfaMethod === "recovery" ? "Recovery code (xxxx-xxxx)" : "6-digit code"}</Label>
          <Input id="mfa-code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder={mfaMethod === "recovery" ? "abcd-1234" : "123456"} required data-testid="mfa-code-input" />
        </div>
        <Button type="submit" disabled={busy || !mfaCode} className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="mfa-verify-button">
          {busy ? "Verifying…" : "Verify & sign in"}
        </Button>
        <button type="button" onClick={() => { setMfa(null); setMfaCode(""); }} className="text-sm text-slate-500 hover:underline w-full text-center" data-testid="mfa-back">Back to login</button>
      </form>
    );
  }

  if (mode === "magic") {
    return (
      <form onSubmit={submit} className="space-y-4" data-testid="magic-link-form">
        <p className="text-sm font-semibold text-slate-800">{t("auth.magicTitle", "Sign in with an email link")}</p>
        <p className="text-sm text-slate-500 -mt-2">{t("auth.magicSub", "Enter your account email — we'll send a one-time sign-in link (good for 15 minutes). No password needed.")}</p>
        <div>
          <Label htmlFor="auth-email">{t("auth.email")}</Label>
          <Input id="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="magic-email-input" />
        </div>
        <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="magic-send-button">
          {busy ? t("common.loading") : t("auth.magicSend", "Send my sign-in link")}
        </Button>
        {magicSent && <p className="text-sm text-emerald-700 font-medium text-center" data-testid="magic-sent-note">{t("auth.magicSent", "Sent — check your inbox. The link works once and expires in 15 minutes.")}</p>}
        <button type="button" onClick={() => { setMode("login"); setMagicSent(false); }} className="text-sm text-slate-500 hover:underline w-full text-center" data-testid="magic-back-button">{t("auth.magicBack", "Back to password sign-in")}</button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
      {mode === "signup" && (
        <div>
          <Label htmlFor="auth-name">{t("auth.name")}</Label>
          <Input id="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.namePh")} data-testid="auth-name-input" />
        </div>
      )}
      <div>
        <Label htmlFor="auth-email">{t("auth.email")}</Label>
        <Input id="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="auth-email-input" />
      </div>
      <div>
        <Label htmlFor="auth-password">{t("auth.password")}</Label>
        <Input id="auth-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "signup" ? t("auth.passwordPhSignup") : t("auth.passwordPhLogin")} data-testid="auth-password-input" />
        {mode === "signup" && <p className="text-xs text-slate-500 mt-1">{t("auth.pwRules")}</p>}
      </div>
      {mode === "signup" && (
        <label className="flex items-start gap-3 text-sm text-slate-600">
          <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="mt-1 accent-emerald-600" data-testid="newsletter-optin-checkbox" />
          <span>{t("auth.newsletterOptIn")}</span>
        </label>
      )}
      <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="auth-submit-button">
        {busy ? t("common.loading") : mode === "signup" ? t("auth.createAccount") : t("auth.login")}
      </Button>
      {mode === "login" && (
        <div className="text-sm text-center space-y-2">
          <p>
            <button type="button" onClick={() => navigate("/forgot-password")} className="text-emerald-700 font-medium hover:underline" data-testid="forgot-password-link">
              {t("auth.forgot")}
            </button>
          </p>
          <p>
            <button type="button" onClick={() => setMode("magic")} className="text-emerald-700 font-medium hover:underline" data-testid="magic-link-cta">
              {t("auth.magicCta", "Email me a sign-in link instead")}
            </button>
          </p>
        </div>
      )}
      <p className="text-sm text-center text-slate-500">
        {mode === "signup" ? t("auth.haveAccount") : t("auth.newHere")}{" "}
        <button type="button" onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-emerald-700 font-medium hover:underline" data-testid="auth-mode-toggle">
          {mode === "signup" ? t("auth.login") : t("auth.createAccount")}
        </button>
      </p>
    </form>
  );
}

export default function AuthPage() {
  return (
    <div className="max-w-md mx-auto px-4 py-16 sm:py-24 fade-in">
      <AuthPageContent />
    </div>
  );
}

function AuthPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  useMeta("Log in | Denial Decoder", "Log in or create your free Denial Decoder account.", { noindex: true });
  const next = new URLSearchParams(location.search).get("next") || "/app/cases";
  return (
    <>
      <img src="/brand/logo-stacked.webp" alt="Denial Decoder" width="248" height="192" className="h-24 w-auto mb-4" data-testid="auth-brand-logo" />
      <h1 className="text-3xl font-extrabold tracking-tight mb-2">{t("auth.title")}</h1>
      <p className="text-slate-600 mb-8">{t("auth.sub")}</p>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        <AuthForm onSuccess={() => navigate(next)} defaultMode="login" />
      </div>
    </>
  );
}
